import { Checkbox, FormControlLabel, FormGroup, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { EMAIL, SMS, SMS_BODY_MAX, type BroadcastChannel } from '../../../lib/broadcastChannels';
import { describeSmsEstimate, smsEstimateDetail, smsOverLimitMessage } from '../../../lib/smsEstimate';
import { useSmsEstimates } from './useSmsEstimates';

export interface ChannelPickerProps {
  channels: BroadcastChannel[];
  onToggle: (channel: BroadcastChannel, on: boolean) => void;
  smsBody: string;
  onSmsBody: (value: string) => void;
  smsBodyError: string | null;
  disabled?: boolean;
  personalizeInviteLinks: boolean;
  onPersonalizeInviteLinks: (on: boolean) => void;
  /** Drives the warning: on an invite-only group the impersonal link is a dead end. */
  inviteOnly: boolean;
  /**
   * The trip the text is about. The server wraps the host's words in "Tripsby -
   * {title}" and the trip link, so it is asked to count the whole text (and a
   * labelled local count stands in meanwhile); without it the counter shows
   * characters only.
   */
  trip?: { groupId: string; title: string; slug: string; locationName?: string | null };
}

/**
 * How a broadcast gets delivered.
 *
 * SMS starts unchecked and stays unchecked unless the host says otherwise: a
 * host who ignores this control gets exactly the email-only broadcast Studio
 * has always sent. The text body is a separate 280-char field rather than a
 * reuse of the email body, because the email body allows 5000 characters and a
 * 5000-character text is thirty-odd billed segments of unreadable message.
 */
export default function ChannelPicker({
  channels,
  onToggle,
  smsBody,
  onSmsBody,
  smsBodyError,
  disabled,
  personalizeInviteLinks,
  onPersonalizeInviteLinks,
  inviteOnly,
  trip,
}: ChannelPickerProps) {
  const smsSelected = channels.includes(SMS);
  // The server counts the text exactly as it will send it; a labelled local count shows while it is asked.
  const { estimateFor } = useSmsEstimates({
    groupId: trip?.groupId ?? '',
    items: trip && smsSelected ? [{ id: 'composer', message: smsBody }] : [],
    personalize: personalizeInviteLinks,
    trip,
  });
  const smsSent = estimateFor('composer');

  return (
    <Stack spacing={1.5}>
      <Stack spacing={0.5}>
        <Typography variant="subtitle2" fontWeight={700}>Send by</Typography>
        <FormGroup row>
          <FormControlLabel
            control={
              <Checkbox
                checked={channels.includes(EMAIL)}
                onChange={(event) => onToggle(EMAIL, event.target.checked)}
                disabled={disabled}
                inputProps={{ 'aria-label': 'Send by email' }}
              />
            }
            label="Email"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={smsSelected}
                onChange={(event) => onToggle(SMS, event.target.checked)}
                disabled={disabled}
                inputProps={{ 'aria-label': 'Send by SMS' }}
              />
            }
            label="SMS (text)"
          />
        </FormGroup>
        {channels.length === 0 ? (
          <Typography variant="caption" color="error">
            Pick at least one channel.
          </Typography>
        ) : null}
      </Stack>

      {smsSelected ? (
        <TextField
          fullWidth
          multiline
          minRows={3}
          label="Text message"
          value={smsBody}
          onChange={(event) => onSmsBody(event.target.value.slice(0, SMS_BODY_MAX))}
          inputProps={{ maxLength: SMS_BODY_MAX }}
          error={Boolean(smsBodyError) || Boolean(smsSent?.source === 'server' && smsSent.overLimit)}
          disabled={disabled}
          helperText={
            smsBodyError
            // The server adds the heading and the trip link, so the host knows the
            // 280 characters they write are not the whole message.
            ?? (
              <>
                {`${smsBody.length}/${SMS_BODY_MAX} · Variables: {name} {title} {location} {url}. `
                  + 'A heading ("Tripsby - the trip name") is added at the start and the trip link at the end.'}
                {smsSent ? (
                  <>
                    {' '}
                    <Tooltip title={smsEstimateDetail(smsSent)}>
                      <span>{describeSmsEstimate(smsSent)}.</span>
                    </Tooltip>
                  </>
                ) : null}
                {smsSent?.overLimit ? ` ${smsOverLimitMessage(smsSent)}` : ''}
              </>
            )
          }
        />
      ) : null}

      <Stack spacing={0.5}>
        <FormControlLabel
          control={
            <Checkbox
              checked={personalizeInviteLinks}
              onChange={(event) => onPersonalizeInviteLinks(event.target.checked)}
              disabled={disabled}
              inputProps={{ 'aria-label': 'Give each guest their own invite link' }}
            />
          }
          label="Give each guest their own link"
        />
        <Typography variant="caption" color={inviteOnly && !personalizeInviteLinks ? 'error' : 'text.secondary'}>
          {inviteOnly && !personalizeInviteLinks
            // Not advice — a statement of what this send will do. This group is
            // invite-only, so the impersonal link resolves to no invitee and the
            // guest page refuses to open. A live trip sent exactly this.
            ? 'This trip is invite-only, so the shared link opens to "this invitation needs its '
              + 'original link" for everyone. Tick this, or make the trip public.'
            : 'Each guest gets a link that signs them in, so they see their own room and can '
              + 'claim a dinner seat. It is forwardable — whoever opens it is treated as that '
              + 'guest — and makes each text a little longer, which can add one more text per guest.'}
        </Typography>
      </Stack>
    </Stack>
  );
}
